import {
  FaCashRegister,
  FaCoins,
  FaIdCard,
  FaRightLeft,
  FaShieldHalved,
  FaStore,
  FaWallet,
} from "react-icons/fa6";

import type { FeatureModule } from "./types";

export const kartuModules: FeatureModule[] = [
  {
    slug: "kartu-siswa",
    label: "Kartu Siswa Digital",
    navDescription: "RFID/QR dengan saldo uang saku",
    icon: FaIdCard,
    group: "kartu",
    summary:
      "Satu kartu untuk identitas, presensi, dan uang saku non-tunai — dengan kontrol penuh di tangan orang tua.",
    detail: {
      title: "Satu kartu untuk *identitas dan uang saku* anak di sekolah",
      lead: "Kartu siswa ber-RFID dengan saldo, limit harian, dan riwayat transaksi yang terbuka untuk orang tua",
      intro:
        "Anak tidak perlu membawa uang tunai. Saldo kartu dipakai di kantin, koperasi, dan toko sekolah, sementara orang tua menentukan limit hariannya dan melihat setiap transaksi dari aplikasi. Kartu hilang tinggal diblokir dari HP — saldonya aman.",
      highlights: [
        "Saldo uang saku terpisah, dengan limit harian yang Anda tentukan",
        "Isi saldo dari Alfamart, toko mitra, transfer bank, atau admin sekolah",
        "Blokir instan, ubah dan reset PIN sendiri dari aplikasi",
      ],
      benefits: [
        {
          title: "Uang saku yang terkendali",
          body: "Orang tua menetapkan limit harian dan menerima notifikasi saat limit tersentuh, jadi uang jajan tidak habis dalam sekali belanja.",
        },
        {
          title: "Isi saldo dari mana saja",
          body: "Top up bisa lewat Alfamart, toko mitra di sekitar sekolah, transfer bank, atau langsung ke admin sekolah — semuanya masuk ke riwayat yang sama.",
        },
        {
          title: "Setiap rupiah ada jejaknya",
          body: "Riwayat transaksi menampilkan sumber, waktu, nominal, dan status setiap pengisian maupun penggunaan. Tidak ada saldo yang hilang tanpa keterangan.",
        },
        {
          title: "Kartu hilang bukan lagi masalah",
          body: "Blokir kartu dari aplikasi, terbitkan kartu pengganti, dan saldo ikut berpindah. PIN bisa diubah atau direset sendiri kapan saja.",
        },
      ],
      screensTitle: "Semuanya dari genggaman orang tua",
      screensLead:
        "Saldo, riwayat, dan pengaturan keamanan kartu ada di satu aplikasi — tanpa perlu datang ke sekolah.",
      screens: [
        {
          src: "/image/fitur/kartu-siswa/mobile-detail-kartu.webp",
          alt: "Halaman detail kartu siswa dengan saldo dan riwayat transaksi",
          caption: "Detail Kartu",
          note: "Saldo uang saku, limit harian, dan riwayat transaksi",
          kind: "mobile",
        },
        {
          src: "/image/fitur/kartu-siswa/mobile-setting-kartu.webp",
          alt: "Pengaturan kartu siswa: limit, PIN, blokir kartu",
          caption: "Setting Kartu",
          note: "Limit harian, PIN, kelola akses, blokir kartu",
          kind: "mobile",
        },
        {
          src: "/image/fitur/kartu-siswa/mobile-topup.webp",
          alt: "Halaman top up kartu siswa di aplikasi CARDS",
          caption: "Top Up Kartu",
          note: "Pilih nominal dan metode pembayaran",
          kind: "mobile",
        },
      ],
      faq: [
        {
          q: "Kartunya bisa pakai desain dan logo sekolah kami?",
          a: "Bisa. Kartu siswa dicetak dengan identitas dan branding sekolah Anda, lengkap dengan foto serta nomor kartu siswa.",
        },
        {
          q: "Kalau kartu hilang, saldonya bagaimana?",
          a: "Orang tua bisa memblokir kartu langsung dari aplikasi sehingga tidak dapat dipakai siapa pun. Saldo tetap tercatat dan dipindahkan ke kartu pengganti.",
        },
        {
          q: "Apakah kartu ini juga dipakai untuk presensi?",
          a: "Ya. Kartu yang sama dipakai untuk tap presensi harian, presensi kegiatan, dan transaksi di kantin maupun koperasi sekolah.",
        },
      ],
      metaTitle: "Kartu Siswa Digital RFID dengan Saldo & Limit Harian",
      metaDescription:
        "Kartu siswa ber-RFID untuk presensi dan uang saku non-tunai. Orang tua atur limit harian, pantau riwayat transaksi, dan blokir kartu langsung dari aplikasi.",
      keywords: [
        "kartu siswa digital",
        "kartu pelajar rfid",
        "kartu siswa dengan saldo",
        "uang saku digital siswa",
        "kartu sekolah cashless",
      ],
    },
  },

  {
    slug: "top-up-kartu",
    label: "Top Up Kartu",
    navDescription: "Alfamart, transfer bank, admin sekolah",
    icon: FaWallet,
    group: "kartu",
    summary:
      "Isi saldo kartu dari jaringan retail, transfer bank, atau loket sekolah — semuanya masuk ke satu riwayat.",
    detail: {
      title: "Isi saldo dari *tempat yang paling dekat* dengan orang tua",
      lead: "Alfamart, toko mitra sekitar sekolah, transfer bank, atau langsung ke admin — semuanya sah",
      intro:
        "Tidak semua orang tua nyaman dengan transfer bank, dan tidak semua sempat datang ke sekolah. Karena itu pengisian saldo dibuka lewat beberapa jalur sekaligus, dari minimarket sampai loket sekolah, dan semuanya bermuara ke riwayat kartu yang sama.",
      highlights: [
        "Pengisian lewat Alfamart dan toko mitra di sekitar sekolah",
        "Transfer bank dan metode pembayaran digital",
        "Top up oleh admin sekolah untuk yang membayar tunai",
      ],
      benefits: [
        {
          title: "Pilih jalur yang paling mudah dijangkau",
          body: "Orang tua yang terbiasa ke minimarket bisa mengisi di Alfamart atau toko mitra; yang terbiasa mobile banking bisa transfer. Tidak ada yang dipaksa satu cara.",
        },
        {
          title: "Yang tunai tetap terlayani",
          body: "Admin sekolah dapat melakukan top up untuk orang tua yang menyerahkan uang tunai, dan transaksinya tetap tercatat sebagai pengisian resmi.",
        },
        {
          title: "Status pengisian terlihat jelas",
          body: "Setiap pengisian punya status tersendiri — sukses, pending, atau gagal — sehingga tidak ada kebingungan saat saldo belum bertambah.",
        },
        {
          title: "Semua bermuara ke satu riwayat",
          body: "Dari jalur mana pun, pengisian masuk ke riwayat transaksi kartu yang sama, lengkap dengan sumber dan waktunya.",
        },
      ],
      screensTitle: "Dua langkah, dari nominal ke metode",
      screensLead:
        "Alur pengisian dibuat pendek supaya orang tua tidak tersesat di tengah jalan.",
      screens: [
        {
          src: "/image/fitur/top-up-kartu/mobile-topup.webp",
          alt: "Halaman top up kartu dengan pilihan nominal",
          caption: "Pilih Nominal",
          note: "Tentukan jumlah saldo yang akan diisi",
          kind: "mobile",
        },
        {
          src: "/image/fitur/top-up-kartu/mobile-metode.webp",
          alt: "Pilihan metode pembayaran untuk top up kartu siswa",
          caption: "Metode Pembayaran",
          note: "Retail, transfer bank, dan pembayaran digital",
          kind: "mobile",
        },
      ],
      faq: [
        {
          q: "Berapa lama saldo masuk setelah pengisian?",
          a: "Pengisian lewat kanal digital umumnya masuk seketika. Setiap transaksi menampilkan statusnya sendiri, sehingga orang tua tahu bila ada yang masih pending.",
        },
        {
          q: "Apakah pengisian lewat Alfamart dikenakan biaya?",
          a: "Biaya layanan mengikuti ketentuan kanal pembayaran yang dipilih. Rinciannya ditampilkan sebelum pembayaran dikonfirmasi.",
        },
        {
          q: "Kalau pengisian gagal, uangnya bagaimana?",
          a: "Transaksi yang gagal tercatat dengan status gagal dan tidak menambah saldo. Riwayatnya tetap tersimpan sehingga mudah ditelusuri bila perlu ditindaklanjuti.",
        },
      ],
      metaTitle: "Top Up Kartu Siswa lewat Alfamart & Transfer Bank",
      metaDescription:
        "Isi saldo kartu siswa dari Alfamart, toko mitra, transfer bank, atau admin sekolah. Semua pengisian masuk ke satu riwayat dengan status yang jelas.",
      keywords: [
        "top up kartu siswa",
        "isi saldo kartu sekolah",
        "top up kartu pelajar alfamart",
        "pengisian saldo siswa",
        "uang saku digital top up",
      ],
    },
  },

  {
    slug: "limit-kartu",
    label: "Limit & Kontrol Orang Tua",
    navDescription: "Limit harian, PIN, blokir kartu",
    icon: FaShieldHalved,
    group: "kartu",
    summary:
      "Limit harian, notifikasi limit, kelola akses, ubah dan reset PIN, sampai blokir kartu dari aplikasi.",
    detail: {
      title: "Kendali uang saku tetap *di tangan orang tua*",
      lead: "Limit harian, notifikasi, PIN, dan blokir kartu — semuanya diatur sendiri dari aplikasi",
      intro:
        "Memberi anak kartu bersaldo tidak berarti melepas kendali. Orang tua menetapkan batas belanja harian, mendapat pemberitahuan saat batas tersentuh, dan bisa memblokir kartu seketika bila hilang. Semua pengaturan ada di satu halaman, tanpa perlu menghubungi sekolah.",
      highlights: [
        "Limit harian yang bisa diubah kapan saja",
        "Notifikasi saat limit belanja tersentuh",
        "Ubah PIN, reset PIN, blokir, sampai hapus kartu",
      ],
      benefits: [
        {
          title: "Batas belanja yang Anda tentukan",
          body: "Tetapkan berapa maksimal anak boleh berbelanja dalam sehari. Angkanya bisa disesuaikan sewaktu-waktu tanpa mengganti kartu.",
        },
        {
          title: "Diberi tahu, bukan baru tahu belakangan",
          body: "Notifikasi limit membuat orang tua sadar saat pola belanja anak berubah, bukan setelah saldo terlanjur habis di akhir bulan.",
        },
        {
          title: "PIN yang bisa diurus sendiri",
          body: "Ubah PIN atau reset PIN dilakukan langsung dari aplikasi. Tidak perlu menghubungi petugas sekolah hanya karena anak lupa PIN.",
        },
        {
          title: "Kelola akses beberapa kartu",
          body: "Satu orang tua bisa menambah kartu, mengatur akses, dan menghapus kartu — praktis bagi yang punya lebih dari satu anak di sekolah yang sama.",
        },
      ],
      screensTitle: "Satu halaman untuk semua kendali",
      screensLead:
        "Pengaturan keamanan dikumpulkan di satu tempat supaya cepat dijangkau saat dibutuhkan mendadak.",
      screens: [
        {
          src: "/image/fitur/limit-kartu/mobile-setting.webp",
          alt: "Halaman setting kartu dengan limit harian, PIN, dan blokir kartu",
          caption: "Setting Kartu",
          note: "Limit harian, PIN, kelola akses, dan blokir",
          kind: "mobile",
        },
      ],
      faq: [
        {
          q: "Apakah limit harian berlaku juga di kantin sekolah?",
          a: "Ya. Limit harian berlaku atas penggunaan saldo kartu, termasuk transaksi di kantin, koperasi, dan toko sekolah.",
        },
        {
          q: "Kalau anak kehilangan kartu di jam sekolah?",
          a: "Orang tua dapat memblokir kartu seketika dari aplikasi, sehingga kartu tidak bisa dipakai siapa pun sejak saat itu juga.",
        },
        {
          q: "Bisakah satu akun orang tua mengelola kartu beberapa anak?",
          a: "Bisa. Tersedia penambahan kartu dan pengaturan akses, sehingga beberapa kartu dapat dikelola dari satu akun yang sama.",
        },
      ],
      metaTitle: "Limit Harian & Kontrol Kartu Siswa oleh Orang Tua",
      metaDescription:
        "Atur limit belanja harian, terima notifikasi limit, ubah dan reset PIN, hingga blokir kartu siswa langsung dari aplikasi orang tua.",
      keywords: [
        "limit harian kartu siswa",
        "kontrol uang saku anak",
        "blokir kartu siswa",
        "pin kartu pelajar",
        "aplikasi orang tua sekolah",
      ],
    },
  },

  {
    slug: "cazhpoin",
    label: "CazhPOIN",
    navDescription: "Poin loyalitas & kelola dana",
    icon: FaCoins,
    group: "kartu",
    summary:
      "Saldo poin yang bisa dibeli dan dikelola, sebagai lapisan terpisah dari uang saku kartu.",
    detail: {
      title: "Saldo poin yang *berdiri sendiri* dari uang saku",
      lead: "Lapisan saldo terpisah di beranda aplikasi, dengan pembelian dan riwayatnya sendiri",
      intro:
        "CazhPOIN adalah saldo poin di tingkat akun orang tua, terpisah dari uang saku yang menempel pada kartu anak. Karena berdiri sendiri, poin bisa dikelola dan dialirkan tanpa mengganggu batas belanja harian yang sudah ditetapkan untuk kartu.",
      highlights: [
        "Saldo poin terpisah dari saldo uang saku kartu",
        "Pembelian poin dan riwayat penggunaannya",
        "Kelola dana dari beranda aplikasi",
      ],
      benefits: [
        {
          title: "Terpisah dari uang saku anak",
          body: "Saldo poin berada di tingkat akun, bukan di kartu. Perubahan poin tidak mengubah limit harian maupun saldo belanja anak.",
        },
        {
          title: "Ada di layar pertama",
          body: "Saldo CazhPOIN tampil di bagian paling atas beranda aplikasi, sehingga tidak perlu dicari ke dalam menu.",
        },
        {
          title: "Riwayat yang bisa ditelusuri",
          body: "Setiap penambahan dan penggunaan poin punya catatannya sendiri, jadi perubahan saldo selalu punya penjelasan.",
        },
        {
          title: "Kelola dana dari satu tempat",
          body: "Menu kelola dana menyatukan pengaturan saldo di tingkat akun, berdampingan dengan pembelian dan riwayat poin.",
        },
      ],
      screensTitle: "CazhPOIN di kepala beranda",
      screensLead:
        "Ditempatkan sebelum kartu siswa, menegaskan bahwa keduanya adalah saldo yang berbeda.",
      screens: [
        {
          src: "/image/fitur/cazhpoin/crop-cazhpoin.webp",
          alt: "Saldo CazhPOIN dengan tombol Beli POIN dan Riwayat di aplikasi CARDS",
          caption: "Saldo CazhPOIN",
          note: "Beli POIN, riwayat, dan kelola dana",
          kind: "crop",
        },
      ],
      faq: [
        {
          q: "Apa bedanya CazhPOIN dengan saldo uang saku di kartu?",
          a: "CazhPOIN adalah saldo di tingkat akun orang tua, sedangkan uang saku menempel pada kartu anak dan tunduk pada limit harian. Keduanya berdiri sendiri.",
        },
        {
          q: "Bagaimana cara menambah CazhPOIN?",
          a: "Lewat menu Beli POIN di beranda aplikasi. Setiap penambahan tercatat pada riwayat sehingga perubahan saldo dapat ditelusuri.",
        },
        {
          q: "Apakah setiap sekolah mengaktifkan CazhPOIN?",
          a: "Ketersediaannya mengikuti konfigurasi layanan pada masing-masing sekolah. Tim kami dapat menjelaskan cakupannya sesuai kebutuhan Anda.",
        },
      ],
      metaTitle: "CazhPOIN — Saldo Poin di Aplikasi CARDS",
      metaDescription:
        "Saldo poin di tingkat akun orang tua, terpisah dari uang saku kartu siswa. Pembelian, riwayat, dan kelola dana langsung dari beranda aplikasi.",
      keywords: [
        "cazhpoin",
        "poin aplikasi sekolah",
        "saldo poin cards",
        "loyalitas orang tua sekolah",
        "kelola dana aplikasi sekolah",
      ],
    },
  },

  {
    slug: "kantin-toko",
    label: "Kantin & Toko Online",
    navDescription: "Transaksi non-tunai & etalase sekolah",
    icon: FaStore,
    group: "kartu",
    summary:
      "Kantin dan koperasi menerima pembayaran dari kartu, plus etalase toko online sekolah di aplikasi orang tua.",
    detail: {
      title: "Kantin tanpa uang tunai, *toko sekolah di dalam aplikasi*",
      lead: "Transaksi kantin dan koperasi memakai saldo kartu, sementara produk sekolah dietalasekan ke orang tua",
      intro:
        "Uang receh di kantin menyulitkan semua pihak — anak kehilangan kembalian, penjaga kantin repot, dan orang tua tidak tahu uangnya ke mana. Dengan saldo kartu, transaksi jadi non-tunai dan terekam. Toko online membawanya selangkah lebih jauh: produk sekolah bisa dilihat dan dipesan dari aplikasi.",
      highlights: [
        "Bayar di kantin dan koperasi dengan tap kartu siswa",
        "Setiap transaksi masuk ke riwayat yang dilihat orang tua",
        "Etalase toko online sekolah lengkap dengan harga",
      ],
      benefits: [
        {
          title: "Tidak ada lagi urusan kembalian",
          body: "Pembayaran memakai saldo kartu, jadi nominalnya tepat. Anak tidak kehilangan uang receh dan penjaga kantin tidak perlu menyiapkan kembalian.",
        },
        {
          title: "Orang tua tahu belanjanya",
          body: "Transaksi kantin masuk ke riwayat kartu, sehingga pola belanja anak terbaca — dan limit harian tetap menjadi pagarnya.",
        },
        {
          title: "Produk sekolah punya etalase",
          body: "Toko online menampilkan produk beserta harga dan asal sekolahnya, jadi orang tua bisa melihat apa yang tersedia tanpa bertanya lebih dulu.",
        },
        {
          title: "Pemasukan unit usaha tercatat",
          body: "Karena transaksinya digital, penerimaan kantin dan koperasi masuk ke catatan sekolah, bukan berakhir di laci kas.",
        },
      ],
      screensTitle: "Etalase sekolah di beranda orang tua",
      screensLead:
        "Produk kantin dan koperasi tampil dengan harga dan asal sekolahnya, sejajar dengan menu lain.",
      screens: [
        {
          src: "/image/fitur/kantin-toko/crop-toko.webp",
          alt: "Etalase Toko Online sekolah di aplikasi CARDS",
          caption: "Toko Online",
          note: "Produk sekolah lengkap dengan harga satuan",
          kind: "crop",
        },
      ],
      faq: [
        {
          q: "Apakah kantin butuh perangkat khusus?",
          a: "Transaksi kantin memakai kartu siswa yang sama, dengan perangkat pembaca yang disiapkan saat implementasi. Tim kami akan menyesuaikan dengan kondisi kantin Anda.",
        },
        {
          q: "Bisakah beberapa vendor kantin dipisahkan pencatatannya?",
          a: "Bisa. Transaksi tercatat digital sehingga penerimaan dapat ditelusuri, dan pembukuan sekolah dapat memisahkan sesuai kesepakatan bagi hasil.",
        },
        {
          q: "Apakah anak bisa berbelanja melebihi limit hariannya?",
          a: "Tidak. Limit harian yang ditetapkan orang tua berlaku atas transaksi kartu, termasuk pembelian di kantin dan koperasi.",
        },
      ],
      metaTitle: "Kantin Cashless & Toko Online Sekolah",
      metaDescription:
        "Transaksi kantin dan koperasi dengan tap kartu siswa, tanpa uang tunai. Etalase toko online sekolah tampil langsung di aplikasi orang tua.",
      keywords: [
        "kantin sekolah cashless",
        "aplikasi kasir kantin sekolah",
        "koperasi sekolah digital",
        "toko online sekolah",
        "pembayaran kantin kartu siswa",
      ],
    },
  },

  {
    slug: "pindah-saldo",
    label: "Pindah Saldo & Rekap",
    navDescription: "Transfer antar kartu & rekap keuangan",
    icon: FaRightLeft,
    group: "kartu",
    summary:
      "Pindahkan saldo antar kartu atau ke tabungan, dengan rekap keuangan per siswa.",
    detail: {
      title: "Saldo yang bisa *dipindahkan*, bukan mengendap di satu kartu",
      lead: "Pindahkan saldo antar kartu dan baca rekap keuangannya dari halaman yang sama",
      intro:
        "Saldo yang tersisa di kartu seorang anak tidak harus terkunci di sana. Fitur Pindah memungkinkan saldo dialihkan, sementara Rekap Keuangan merangkum pemasukan dan penggunaan agar orang tua tidak perlu menghitung sendiri dari daftar transaksi yang panjang.",
      highlights: [
        "Pindahkan saldo dari satu kartu ke tujuan lain",
        "Rekap keuangan merangkum pemasukan dan penggunaan",
        "Penerimaan dari kartu lain tercatat pada riwayat",
      ],
      benefits: [
        {
          title: "Saldo tidak terkunci",
          body: "Sisa saldo bisa dipindahkan, misalnya ke kartu adik atau saat siswa berganti kartu — tanpa harus melapor dan menunggu diproses manual.",
        },
        {
          title: "Rekap yang menghemat waktu",
          body: "Alih-alih menelusuri riwayat satu per satu, rekap keuangan sudah merangkum berapa yang masuk dan berapa yang terpakai.",
        },
        {
          title: "Perpindahan ada catatannya",
          body: "Penerimaan dari kartu lain muncul di riwayat dengan nomor kartu asalnya, sehingga sumber saldo selalu bisa ditelusuri.",
        },
        {
          title: "Satu halaman dengan saldo",
          body: "Top up, rekap keuangan, dan pindah saldo diletakkan berdampingan di halaman detail kartu — tidak perlu berpindah menu.",
        },
      ],
      screensTitle: "Pindah dan rekap dari detail kartu",
      screensLead:
        "Ketiga aksi utama diletakkan tepat di bawah saldo, dengan riwayat lengkap di bawahnya.",
      screens: [
        {
          src: "/image/fitur/pindah-saldo/mobile-detail-kartu.webp",
          alt: "Detail kartu dengan tombol Top Up, Rekap Keuangan, dan Pindah",
          caption: "Detail Kartu",
          note: "Top Up, Rekap Keuangan, dan Pindah dalam satu baris",
          kind: "mobile",
        },
      ],
      faq: [
        {
          q: "Saldo bisa dipindahkan ke mana saja?",
          a: "Perpindahan ditujukan antar kartu dalam ekosistem CARDS. Setiap penerimaan tercatat di riwayat kartu tujuan beserta nomor kartu asalnya.",
        },
        {
          q: "Apakah perpindahan saldo dikenakan biaya?",
          a: "Ketentuan biaya mengikuti konfigurasi layanan sekolah. Rinciannya ditampilkan sebelum perpindahan dikonfirmasi.",
        },
        {
          q: "Apa isi Rekap Keuangan?",
          a: "Rekap merangkum pemasukan dan penggunaan saldo kartu pada periode tertentu, sebagai ringkasan atas riwayat transaksi yang lebih rinci.",
        },
      ],
      metaTitle: "Pindah Saldo Kartu & Rekap Keuangan Siswa",
      metaDescription:
        "Pindahkan saldo antar kartu siswa dan baca rekap pemasukan serta penggunaannya. Setiap perpindahan tercatat lengkap dengan kartu asalnya.",
      keywords: [
        "pindah saldo kartu siswa",
        "transfer saldo kartu sekolah",
        "rekap keuangan siswa",
        "mutasi saldo kartu pelajar",
        "riwayat transaksi kartu siswa",
      ],
    },
  },
  {
    slug: "kasir-kantin",
    label: "Aplikasi Kasir Kantin",
    navDescription: "CazhPOS: kasir, stok, pre-order",
    icon: FaCashRegister,
    group: "kartu",
    summary:
      "CazhPOS — aplikasi kasir untuk pedagang kantin dan koperasi: transaksi, stok, pre-order, sampai pencairan saldo.",
    detail: {
      title: "Kasir kantin yang *sejalan dengan kartu siswa*",
      lead: "CazhPOS melayani sisi pedagang: transaksi kasir, produk dan stok, pre-order, sampai pencairan hasil jualan",
      intro:
        "Kantin cashless butuh dua sisi: siswa yang membayar dengan kartu, dan pedagang yang melayaninya. CazhPOS adalah sisi pedagang itu — aplikasi kasir tempat transaksi diterima, stok dikelola, dan hasil penjualan dicairkan, semuanya tersambung ke ekosistem kartu CARDS.",
      highlights: [
        "Terima pembayaran dari kartu siswa langsung di kasir",
        "Kelola produk, stok, toko, dan karyawan dari satu aplikasi",
        "Rekap kasir dan laporan, sampai pencairan saldo toko",
      ],
      benefits: [
        {
          title: "Transaksi tanpa uang tunai di meja kasir",
          body: "Pembayaran dari kartu siswa masuk sebagai transaksi kasir yang tercatat — tidak ada lagi urusan uang receh dan kembalian di jam istirahat yang padat.",
        },
        {
          title: "Stok dan produk terkendali",
          body: "Daftar produk beserta stoknya dikelola dari aplikasi, sehingga pedagang tahu apa yang laku dan kapan harus mengisi ulang.",
        },
        {
          title: "Pre-order sebelum jam istirahat",
          body: "Pesanan bisa masuk lebih dulu lewat pre-order, mengurai antrean kantin di jam ramai.",
        },
        {
          title: "Hasil jualan jelas dan bisa dicairkan",
          body: "Rekap kasir dan laporan menunjukkan penjualan per periode, piutang pelanggan tercatat, dan saldo toko dapat dicairkan — alur yang sama yang terlihat pada video pencairan saldo pedagang di pesantren mitra.",
        },
      ],
      screensTitle: "CazhPOS dan CazhTap di meja kasir",
      screensLead:
        "Dua sisi yang membuat kantin cashless berjalan: aplikasi kasir di tangan pedagang, dan perangkat tap untuk kartu siswa.",
      screens: [
        {
          src: "/image/fitur/kasir-kantin/pos-showcase.webp",
          alt: "Dashboard CazhPOS dengan grafik penjualan, produk, saldo toko, dan aplikasi ponselnya",
          caption: "Aplikasi CazhPOS",
          note: "Dashboard penjualan, produk, karyawan, dan saldo toko — di web maupun ponsel pedagang",
          kind: "crop",
        },
        {
          src: "/image/fitur/kasir-kantin/cazhtap-foto.webp",
          alt: "Perangkat CazhTap untuk tap kartu siswa di meja kasir",
          caption: "Perangkat CazhTap",
          note: "Siswa cukup menempelkan kartunya di CazhTap; transaksi masuk ke kasir seketika",
          kind: "crop",
        },
        {
          src: "/image/fitur/kasir-kantin/pos-pembayaran.webp",
          alt: "Pembayaran QRIS dan pilihan metode pembayaran pada aplikasi CazhPOS",
          caption: "Pembayaran fleksibel",
          note: "Selain saldo kartu, kasir menerima QRIS, tunai, dan metode non-tunai lainnya",
          kind: "crop",
        },
      ],
      faq: [
        {
          q: "Siapa yang memakai CazhPOS — sekolah atau pedagang?",
          a: "Pedagang kantin dan koperasi. Sekolah mengelola sisi kartu dan siswanya, sedangkan CazhPOS menjadi alat kerja harian penjual di lingkungan lembaga.",
        },
        {
          q: "Apakah bisa dipakai lebih dari satu karyawan?",
          a: "Bisa. CazhPOS mendukung pengelolaan toko dan karyawan, sehingga beberapa kasir dapat melayani bergantian pada toko yang sama.",
        },
        {
          q: "Bagaimana pedagang menerima uangnya?",
          a: "Hasil penjualan terkumpul sebagai saldo toko yang dapat dicairkan melalui alur penarikan yang tercatat, lengkap dengan rekap penjualannya.",
        },
      ],
      metaTitle: "Aplikasi Kasir Kantin Sekolah & Pesantren",
      metaDescription:
        "CazhPOS: aplikasi kasir untuk pedagang kantin dan koperasi sekolah — terima pembayaran kartu siswa, kelola stok dan pre-order, rekap dan cairkan hasil jualan.",
      keywords: [
        "aplikasi kasir kantin sekolah",
        "pos kantin pesantren",
        "aplikasi kasir koperasi sekolah",
        "kasir cashless sekolah",
        "aplikasi pedagang kantin",
      ],
    },
  },
];
